import { z } from 'zod';

import { limitPolicySchema, limitStatusSchema } from './limits';
import { sessionEndReasonSchema } from './session';

/**
 * Contract between `arena-api` and `arena-agent` (the app on the Arena PCs).
 * The agent authenticates with its workstation token
 * (`Authorization: Bearer <token>`); the C# side mirrors these shapes in
 * `Arena.Agent.Core/Api/Contracts.cs`. JSON is camelCase, instants are ISO-8601 UTC.
 */

const instant = z.string().datetime({ offset: true });

/** `GET /api/agent/roster`: everyone who may unlock a PC, cached by the agent for offline use. */
export const rosterEntrySchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  /** argon2id PHC string. The agent verifies the PIN locally so unlock works offline. */
  pinHash: z.string(),
  /** False without an active guardian authorization (RF-D5) or when deactivated. */
  canUnlock: z.boolean(),
});
export type RosterEntry = z.infer<typeof rosterEntrySchema>;

export const rosterResponseSchema = z.object({
  arenaId: z.string().uuid(),
  arenaName: z.string(),
  workstationId: z.string().uuid(),
  workstationName: z.string(),
  policy: limitPolicySchema,
  youths: z.array(rosterEntrySchema),
  generatedAt: instant,
});
export type RosterResponse = z.infer<typeof rosterResponseSchema>;

/**
 * Events the agent queues locally and sends in batches. `id` is generated on
 * the PC, and ingestion is idempotent on it: resending the queue never
 * duplicates anything.
 */
export const agentEventSchema = z.discriminatedUnion('type', [
  z.object({
    id: z.string().uuid(),
    type: z.literal('session_opened'),
    occurredAt: instant,
    sessionId: z.string().uuid(),
    /** Null for an event unlock (RF-C2): a session that counts for nobody. */
    youthAccountId: z.string().uuid().nullable(),
    /** The unlock happened during the youth's mandatory break (RN-B9). */
    duringBreak: z.boolean().default(false),
  }),
  z.object({
    id: z.string().uuid(),
    type: z.literal('session_closed'),
    occurredAt: instant,
    sessionId: z.string().uuid(),
    reason: sessionEndReasonSchema,
    /** The end time to record; for IDLE_TIMEOUT this is the last input, not the timeout. */
    endedAt: instant,
  }),
  z.object({
    id: z.string().uuid(),
    type: z.literal('command_acknowledged'),
    occurredAt: instant,
    commandId: z.string().uuid(),
    /** `warned`: the 5 minute warning is on screen. `executed`: done. */
    stage: z.enum(['received', 'warned', 'executed']),
  }),
  z.object({
    id: z.string().uuid(),
    type: z.literal('pin_rejected'),
    occurredAt: instant,
    youthAccountId: z.string().uuid().nullable(),
    reason: z.enum(['wrong_pin', 'locked_out', 'no_authorization']),
  }),
]);
export type AgentEvent = z.infer<typeof agentEventSchema>;

export const agentEventBatchSchema = z.object({
  events: z.array(agentEventSchema).min(1).max(500),
});
export type AgentEventBatch = z.infer<typeof agentEventBatchSchema>;

export const agentEventBatchResponseSchema = z.object({
  /** Every id the server now holds, new or already seen. The agent drops them from its queue. */
  accepted: z.array(z.string().uuid()),
  /** Ids rejected for good (malformed for this workstation); the agent drops these too and logs them. */
  rejected: z.array(z.object({ id: z.string().uuid(), error: z.string() })),
});
export type AgentEventBatchResponse = z.infer<typeof agentEventBatchResponseSchema>;

export const remoteCommandKindSchema = z.enum(['lock', 'unlock_event']);
export type RemoteCommandKind = z.infer<typeof remoteCommandKindSchema>;

export const remoteCommandSchema = z.object({
  id: z.string().uuid(),
  kind: remoteCommandKindSchema,
  issuedAt: instant,
  /** For `lock`: when to lock. Five minutes after issue when a youth is in session (RN-C2). */
  executeAt: instant,
  message: z.string().nullable(),
});
export type RemoteCommand = z.infer<typeof remoteCommandSchema>;

/** `POST /api/agent/heartbeat`, every minute. */
export const heartbeatRequestSchema = z.object({
  at: instant,
  sessionId: z.string().uuid().nullable(),
  agentVersion: z.string().max(64).optional(),
});
export type HeartbeatRequest = z.infer<typeof heartbeatRequestSchema>;

export const heartbeatResponseSchema = z.object({
  serverTime: instant,
  commands: z.array(remoteCommandSchema),
  /** Limits of the youth in the current session, for the warnings on screen. */
  limits: limitStatusSchema.nullable(),
  /** Changes when the roster changes; the agent refetches when it differs from its cache. */
  rosterVersion: z.string(),
});
export type HeartbeatResponse = z.infer<typeof heartbeatResponseSchema>;

/** `GET /api/agent/youths/:accountId/limits`, asked right after an unlock. */
export const unlockCheckResponseSchema = z.object({
  limits: limitStatusSchema,
});
